import React, { useState, lazy } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Scan, Languages, Calendar, MessageCircle, Phone, CheckCircle2, ArrowRight, Plus, Minus, Microscope, ShieldCheck, CreditCard, Heart, Users, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
const MAPS_URL = 'https://maps.app.goo.gl/Fhof2j1jnLA3ffqT6';
const MAPS_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1241.0181846982155!2d-0.104187760804084!3d51.53089276153758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760f7c6534e077%3A0xa54a590bd3b2a149!2sDentico%20Clinic%20-%20Private%20Dentist%20in%20Angel%2C%20Islington!5e0!3m2!1suk!2suk!4v1769722166469!5m2!1suk!2suk';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-2985">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-2986" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-2987">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-2988">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-2989">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-2990">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-2991" /> : <Plus className="w-4 h-4" data-id="element-2992" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-2993">
        <p className="text-gray-600 leading-relaxed" data-id="element-2994">{answer}</p>
      </div>
    </div>;
}
export function RussianDentistPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const services = [{
    id: 'fillings',
    title: 'Пломбирование зубов',
    desc: 'Эстетичные фотополимерные пломбы, которые выглядят естественно.',
    icon: Microscope
  }, {
    id: 'root-canal',
    title: 'Лечение каналов',
    desc: 'Безболезненное лечение под микроскопом для сохранения зуба.',
    icon: Scan
  }, {
    id: 'crowns',
    title: 'Коронки и протезирование',
    desc: 'Керамические коронки и мосты для восстановления функции.',
    icon: ShieldCheck
  }, {
    id: 'hygiene',
    title: 'Профессиональная гигиена',
    desc: 'Чистка Airflow и ультразвук для здоровья десен.',
    icon: Star
  }, {
    id: 'whitening',
    title: 'Отбеливание зубов',
    desc: 'Безопасное осветление эмали на несколько тонов.',
    icon: Star
  }, {
    id: 'extraction',
    title: 'Удаление зубов',
    desc: 'Атравматичное удаление, включая зубы мудрости.',
    icon: ShieldCheck
  }, {
    id: 'smile-makeover',
    title: 'Smile Makeover',
    desc: 'Комплексная эстетическая трансформация улыбки.',
    icon: Star
  }, {
    id: 'cosmetic',
    title: 'Инъекционная косметология',
    desc: 'Эстетические процедуры для гармонии лица.',
    icon: Star
  }];
  const faqs = [{
    q: 'Есть ли русскоязычный стоматолог в Лондоне?',
    a: 'Да. В Dentico Clinic вы можете получить консультацию и лечение на русском языке в центре Лондона — район Angel / Islington (EC1V).'
  }, {
    q: 'Где находится Dentico Clinic?',
    a: 'Наш адрес: 360–364 City Road, London EC1V 2PY. Клиника расположена рядом с Angel Station (Islington).'
  }, {
    q: 'Как записаться на приём?',
    a: 'Вы можете записаться онлайн через сайт или связаться с нами через WhatsApp/Telegram. Также доступна запись по телефону: +44 7345 197820.'
  }, {
    q: 'Какие услуги предоставляет ваша стоматология?',
    a: 'Dentico Clinic предоставляет полный спектр стоматологических услуг: консультации, профессиональная гигиена, лечение кариеса, лечение каналов, коронки, виниры, имплантация и неотложная помощь.'
  }, {
    q: 'Принимаете ли вы пациентов из других районов Лондона?',
    a: "Да. К нам удобно добраться из Islington, King's Cross, Old Street, Shoreditch и Central London."
  }, {
    q: 'Можно ли получить консультацию на русском языке?',
    a: 'Да, в Dentico Clinic возможно общение на русском языке во время консультации и лечения.'
  }, {
    q: 'Делаете ли вы срочные визиты при острой боли?',
    a: 'Да. Если у вас сильная боль, свяжитесь с нами как можно скорее — мы постараемся найти ближайшее доступное время.'
  }, {
    q: 'Что искать в Google, чтобы найти вашу клинику?',
    a: 'Попробуйте запросы: "русский стоматолог Лондон", "стоматология на русском Лондон", "Russian dentist London", "dentist near Angel station".'
  }];
  // FAQPage structured data for SEO
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <Layout data-id="element-2995">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-2996" />

      {/* FAQ Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-2997" />

      {/* 1. HERO SECTION */}
      <section className="relative bg-white overflow-hidden" data-id="element-2998">
        <div className="absolute inset-0 bg-teal-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-2999" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-3000">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3001">
            <div className="space-y-6" data-id="element-3002">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-800 text-sm font-medium" data-id="element-3003">
                <span className="relative flex h-2 w-2" data-id="element-3004">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-3005"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" data-id="element-3006"></span>
                </span>
                Русскоязычный стоматолог в Лондоне
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-3007">
                Русскоязычный стоматолог в Лондоне –{' '}
                <span className="text-teal-600" data-id="element-3008">Dentico Clinic</span>
                <br data-id="element-3009" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-3010">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-3011">
                Современная цифровая стоматология в центре Лондона с
                консультацией на русском языке. Angel Station, EC1V 2PY.
              </p>

              {/* Trust Badges Row */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-gray-700" data-id="element-3012">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3013">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" data-id="element-3014" />
                  5★ Google Reviews
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3015">
                  <MapPin className="w-4 h-4 text-teal-600" data-id="element-3016" />
                  Central London, Angel
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3017">
                  <Scan className="w-4 h-4 text-teal-600" data-id="element-3018" />
                  Цифровая стоматология
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3019">
                  <Languages className="w-4 h-4 text-teal-600" data-id="element-3020" />
                  Русский язык
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-3021">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3022">
                  <Button size="lg" className="w-full gap-2 shadow-lg shadow-teal-600/20" data-id="element-3023">
                    <Calendar className="w-5 h-5" data-id="element-3024" />
                    Записаться онлайн
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3025">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-teal-200 text-teal-700 hover:bg-teal-50" data-id="element-3026">
                    <MessageCircle className="w-5 h-5 text-green-600" data-id="element-3027" />
                    WhatsApp
                  </Button>
                </a>
                <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3028">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-teal-200 text-teal-700 hover:bg-teal-50" data-id="element-3029">
                    <TelegramIcon className="w-5 h-5 text-blue-500" data-id="element-3030" />
                    Telegram
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-3031">
              <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-3032"></div>
              <img src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=1200&q=80" alt="Русскоязычный стоматолог в Лондоне - Dentico Clinic" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full" data-id="element-3033" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHO WE ARE */}
      <section className="py-16 bg-white" data-id="element-3034">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3035">
          <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3036">
            Кто мы — Dentico Clinic
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-3037">
            Dentico Clinic — это современная частная стоматологическая клиника в
            районе Angel (Central London), где русскоязычные пациенты могут
            получить лечение на родном языке. Мы сочетаем британские стандарты
            качества, передовое цифровое оборудование и искреннюю заботу о
            каждом пациенте. Наши врачи зарегистрированы в GDC (General Dental
            Council) и имеют многолетний опыт работы в Великобритании.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 text-left" data-id="element-3038">
            <div className="bg-gray-50 p-5 rounded-xl border border-gray-100" data-id="element-3039">
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-3040">
                Современное оборудование
              </h3>
              <p className="text-sm text-gray-600" data-id="element-3041">
                Цифровые сканеры, микроскопы и 3D-диагностика для точного
                лечения.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-xl border border-gray-100" data-id="element-3042">
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-3043">Опытные врачи</h3>
              <p className="text-sm text-gray-600" data-id="element-3044">
                Квалифицированные специалисты с опытом работы в частной практике
                Лондона.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-xl border border-gray-100" data-id="element-3045">
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-3046">Прозрачные цены</h3>
              <p className="text-sm text-gray-600" data-id="element-3047">
                Честная стоимость лечения без скрытых платежей. Прайс доступен
                онлайн.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. WHY RUSSIANS CHOOSE US */}
      <section className="py-16 bg-gray-50" data-id="element-3048">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3049">
          <div className="text-center mb-12" data-id="element-3050">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3051">
              Почему русскоязычные пациенты выбирают Dentico Clinic
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-3052">
              Мы понимаем важность общения на родном языке, особенно когда речь
              идет о здоровье.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3053">
            {[{
            icon: Languages,
            title: 'Русский язык',
            desc: 'Вам не нужно беспокоиться о языковом барьере. Мы объясним всё понятно.'
          }, {
            icon: Microscope,
            title: 'Современное оборудование',
            desc: 'Лечение под микроскопом и цифровые оттиски без дискомфорта.'
          }, {
            icon: CreditCard,
            title: 'Прозрачные цены',
            desc: 'Вы всегда знаете стоимость лечения до его начала.'
          }, {
            icon: MapPin,
            title: 'Удобное расположение',
            desc: 'Всего 5 минут от станции Angel (Northern Line), Central London.'
          }, {
            icon: Calendar,
            title: 'Онлайн-запись',
            desc: 'Удобная система бронирования 24/7 без звонков.'
          }, {
            icon: ShieldCheck,
            title: 'Опытные врачи',
            desc: 'Высокие стандарты стерилизации и безопасности (HTM 01-05).'
          }].map((item, idx) => <Card key={idx} className="p-6 hover:shadow-md transition-shadow" data-id="element-3054">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mb-4" data-id="element-3055">
                  <item.icon className="w-6 h-6" data-id="element-3056" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-3057">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-3058">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 3.5 COMMUNITY HUB SECTION */}
      <section className="py-16 bg-white border-t border-gray-100" data-id="element-3059">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3060">
          <div className="text-center mb-12" data-id="element-3061">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3062">
              Для русскоязычных в Лондоне
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-3063">
              Мы понимаем, как важно иметь надёжного стоматолога, когда вы
              далеко от дома.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-id="element-3064">
            {[{
            icon: Heart,
            title: 'Мы понимаем ваши потребности',
            desc: 'Многие наши пациенты — русскоязычные, которые недавно переехали в Лондон. Мы знаем, с какими трудностями вы сталкиваетесь, и создаём комфортную атмосферу.'
          }, {
            icon: Languages,
            title: 'Объясняем лечение на русском',
            desc: 'Никакого языкового барьера. Мы подробно объясняем диагноз, план лечения и все варианты на понятном языке.'
          }, {
            icon: Users,
            title: 'Работаем с новоприбывшими',
            desc: 'Если вы недавно переехали в UK и не знаете, как работает стоматология здесь — мы поможем разобраться в системе и найти лучшее решение.'
          }, {
            icon: BookOpen,
            title: 'Разъясняем систему UK стоматологии',
            desc: 'NHS vs частная стоматология, как выбрать врача, что покрывает страховка — ответим на все ваши вопросы.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-teal-50 border-teal-100" data-id="element-3065">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-teal-600 mb-4 shadow-sm" data-id="element-3066">
                  <item.icon className="w-5 h-5" data-id="element-3067" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-3068">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-3069">
                  {item.desc}
                </p>
              </Card>)}
          </div>

          <div className="bg-teal-900 rounded-2xl p-8 text-center max-w-4xl mx-auto" data-id="element-3070">
            <p className="text-lg text-teal-50 font-medium leading-relaxed" data-id="element-3071">
              "Dentico Clinic — это не просто клиника, а место, где вас поймут.
              Мы гордимся тем, что стали надёжным стоматологическим партнёром
              для русскоязычного сообщества в Лондоне. Запишитесь на
              консультацию — и убедитесь сами."
            </p>
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA */}
      <section className="py-12 bg-teal-600" data-id="element-3072">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-3073">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4" data-id="element-3074">
            Готовы записаться на приём?
          </h2>
          <p className="text-teal-100 mb-6" data-id="element-3075">
            Выберите удобный способ записи — онлайн, через мессенджер или
            закажите обратный звонок.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-3076">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-3077">
              <Button size="lg" className="gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-3078">
                <Calendar className="w-5 h-5" data-id="element-3079" />
                Записаться онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-3080">
              <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" data-id="element-3081">
                <MessageCircle className="w-5 h-5" data-id="element-3082" />
                WhatsApp
              </Button>
            </a>
            <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" onClick={() => setIsCallbackOpen(true)} data-id="element-3083">
              <Phone className="w-5 h-5" data-id="element-3084" />
              Заказать звонок
            </Button>
          </div>
        </div>
      </section>

      {/* 4. SERVICES */}
      <section className="py-16 bg-gray-50" data-id="element-3085">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3086">
          <div className="text-center mb-12" data-id="element-3087">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3088">
              Наши стоматологические услуги в Лондоне
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-3089">
              Полный спектр современной стоматологии для всей семьи в Angel,
              Islington (EC1V).
            </p>
          </div>

          {/* Featured SEO service pages */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-id="element-3090">
            {[{
            to: '/ru/implantaciya-london',
            title: 'Имплантация зубов',
            desc: 'Восстановление утраченных зубов с помощью современных имплантов премиум-класса.',
            badge: 'Популярное'
          }, {
            to: '/ru/viniry-london',
            title: 'Виниры',
            desc: 'Керамические и композитные виниры для идеальной улыбки.',
            badge: 'Эстетика'
          }, {
            to: '/ru/lechenie-kanalov-london',
            title: 'Лечение каналов',
            desc: 'Безболезненная эндодонтия под микроскопом для сохранения зуба.',
            badge: 'Лечение'
          }, {
            to: '/ru/neotlozhnaya-stomatologiya-london',
            title: 'Неотложная помощь',
            desc: 'Срочный приём при острой боли, травме или отеке.',
            badge: 'Срочно'
          }].map((item, idx) => <Link key={idx} to={item.to} className="group block" data-id="element-3091">
                <Card className="h-full p-6 border-2 border-teal-100 hover:border-teal-400 hover:shadow-lg transition-all" data-id="element-3092">
                  <span className="inline-block text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full mb-3" data-id="element-3093">
                    {item.badge}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors" data-id="element-3094">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4" data-id="element-3095">{item.desc}</p>
                  <div className="flex items-center text-sm font-medium text-teal-600" data-id="element-3096">
                    Подробнее <ArrowRight className="w-4 h-4 ml-1" data-id="element-3097" />
                  </div>
                </Card>
              </Link>)}
          </div>

          {/* Other services grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3098">
            {services.map((service, idx) => <Link key={idx} to={`/ru/services/${service.id}`} className="group block" data-id="element-3099">
                <Card className="h-full p-6 hover:border-teal-200 hover:shadow-lg transition-all" data-id="element-3100">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 mb-4 group-hover:bg-teal-100 group-hover:text-teal-600 transition-colors" data-id="element-3101">
                    <service.icon className="w-5 h-5" data-id="element-3102" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors" data-id="element-3103">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4" data-id="element-3104">{service.desc}</p>
                  <div className="flex items-center text-sm font-medium text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3105">
                    Подробнее <ArrowRight className="w-4 h-4 ml-1" data-id="element-3106" />
                  </div>
                </Card>
              </Link>)}
          </div>
          <div className="text-center mt-10" data-id="element-3107">
            <Link to="/ru/services" data-id="element-3108">
              <Button variant="outline" size="lg" data-id="element-3109">
                Все услуги клиники
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 5. LOCATION */}
      <section className="py-16 bg-gray-50" data-id="element-3110">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3111">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100" data-id="element-3112">
            <div className="grid lg:grid-cols-2" data-id="element-3113">
              <div className="p-8 lg:p-12 flex flex-col justify-center" data-id="element-3114">
                <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3115">
                  Где находится клиника
                </h2>
                <div className="space-y-6" data-id="element-3116">
                  <div className="flex items-start gap-4" data-id="element-3117">
                    <div className="p-3 bg-teal-50 rounded-lg text-teal-600 shrink-0" data-id="element-3118">
                      <MapPin className="w-6 h-6" data-id="element-3119" />
                    </div>
                    <div data-id="element-3120">
                      <h3 className="font-bold text-gray-900 mb-1" data-id="element-3121">Адрес</h3>
                      <p className="text-gray-600" data-id="element-3122">360-364 City Road</p>
                      <p className="text-gray-600" data-id="element-3123">London EC1V 2PY</p>
                      <p className="text-sm text-teal-600 mt-2 font-medium" data-id="element-3124">
                        5 минут от Angel Station
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-3125">
                    <div className="p-3 bg-teal-50 rounded-lg text-teal-600 shrink-0" data-id="element-3126">
                      <Calendar className="w-6 h-6" data-id="element-3127" />
                    </div>
                    <div data-id="element-3128">
                      <h3 className="font-bold text-gray-900 mb-1" data-id="element-3129">
                        Часы работы
                      </h3>
                      <p className="text-gray-600" data-id="element-3130">Пн - Сб: 10:00 - 18:00</p>
                      <p className="text-gray-600" data-id="element-3131">Вс: Закрыто</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-3132">
                  <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" data-id="element-3133">
                    <Button size="lg" className="gap-2 w-full sm:w-auto" data-id="element-3134">
                      <MapPin className="w-4 h-4" data-id="element-3135" />
                      Маршрут
                    </Button>
                  </a>
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-3136">
                    <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-3137">
                      <Calendar className="w-4 h-4" data-id="element-3138" />
                      Записаться
                    </Button>
                  </a>
                </div>
              </div>
              <div className="h-[400px] lg:h-auto bg-gray-200 relative" data-id="element-3139">
                <iframe src={MAPS_EMBED_URL} width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Dentico Clinic Location" className="absolute inset-0" data-id="element-3140"></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. FAQ SECTION - SEO CRITICAL */}
      <section className="py-16 bg-white" data-id="element-3141">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3142">
          <div className="text-center mb-10" data-id="element-3143">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3144">
              Частые вопросы
            </h2>
            <p className="text-gray-600" data-id="element-3145">
              Ответы на популярные запросы о русскоязычном стоматологе в
              Лондоне.
            </p>
          </div>

          <div className="space-y-2" data-id="element-3146">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-3147" />)}
          </div>
        </div>
      </section>

      {/* 7. BOOKING CTA */}
      <section className="py-16 bg-teal-900" data-id="element-3148">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-3149">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-3150">
            Запишитесь на приём сегодня
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-3151">
            Позвоните, напишите или запишитесь онлайн — мы ответим на русском.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-3152">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3153">
              <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-3154">
                <Calendar className="w-5 h-5" data-id="element-3155" />
                Записаться онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3156">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-3157">
                <MessageCircle className="w-5 h-5" data-id="element-3158" />
                WhatsApp
              </Button>
            </a>
            <div className="w-full sm:w-auto" data-id="element-3159">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" onClick={() => setIsCallbackOpen(true)} data-id="element-3160">
                <Phone className="w-5 h-5" data-id="element-3161" />
                Заказать звонок
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}